import type { CSSProperties, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Clock, Calendar, Users, Settings, Bell, X, Trophy, LogOut,
} from 'lucide-react';
import TourOverlay from '../tour/TourOverlay';
import ShellBrandBar from './ShellBrandBar';
import { ModeToggle } from '../mode-toggle';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../../hooks/useCompany';
import { createPortal } from 'react-dom';
import { computeDropdownAnchor } from '../../lib/dropdownAnchor';
import type { UserRole } from '../../types/user.types';
import TwoFactorGraceBanner from '../auth/TwoFactorGraceBanner';
import { useEffect, useRef, useState } from 'react';
import { getTodayRecord } from '../../services/attendance.service';
import { getPendingRequests as getWfhPendingRequests } from '../../services/wfh.service';
import { bellBadgeTotal, withAllNoticesRead, withNoticeRead } from '../../lib/notices';
import { getMyNotices, markNoticeRead, markAllNoticesRead, type UserNotice } from '../../services/notices.service';
import { getPendingRequests as getLeavePendingRequests } from '../../services/leave.service';
import { getJobPostings } from '../../services/hiring.service';
import { apiClient } from '../../services/api';
import { getNavItems, type NavItem } from './getNavItems';
import { useModulePermissions } from '../../context/ModulePermissionsContext';

interface AppShellProps {
  children: ReactNode;
}

const MANAGER_ROLES: UserRole[] = ['manager', 'hr_admin'];
const LEAVE_ROLES: UserRole[] = ['staff', 'manager', 'hr_admin'];

/**
 * Routes that render without the shell — no sidebar, no bottom nav.
 *
 * AppShell is mounted once around the whole route tree (App.tsx) rather than by
 * each page, so it has to know which screens are deliberately chrome-less.
 * Auth screens have no signed-in user to build a nav for; the wizard and
 * face-enrol are full-screen flows meant to be finished before navigating away.
 *
 * Matched as prefixes, so nested paths under these render bare too.
 */
const SHELL_LESS_PATHS = [
  '/login',
  '/enrol-face',
  '/onboarding-wizard',
  '/__shims__',
  '/__sentry-test-dev__',
];

/**
 * The sidebar's width, stated once. The <aside> reads it and so does <main>'s
 * left offset, so the two cannot drift — they used to be independent
 * `width: 220` and `marginLeft: 220` literals.
 */
const SHELL_VARS = { '--app-sidebar-w': '220px' } as CSSProperties;

/**
 * Gates the first-run tour only — NOT layout. The shell is a single tree with
 * `md:` breakpoints; branching layout on this is what caused the whole shell to
 * remount when a window crossed 768px. Every tour step after the welcome card
 * points at a sidebar item, which is display:none below md, so on a phone the
 * tour would highlight nothing.
 */
/** sessionStorage can throw (private windows, blocked storage); the tour guard must not. */
function readSessionFlag(key: string): boolean {
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
}
function writeSessionFlag(key: string): void {
  try { sessionStorage.setItem(key, '1'); } catch { /* guard is best effort */ }
}

function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isDesktop;
}

function getRoleLabel(role: UserRole): string {
  switch (role) {
    case 'hr_admin': return 'HR Administrator';
    case 'manager': return 'Manager';
    case 'it_admin': return 'IT Admin';
    case 'staff': return 'Staff';
    case 'super_admin': return 'Super Admin';
    default: return 'Staff';
  }
}

function getUserInitials(fullName: string): string {
  return fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join('');
}

// B9 — getNavItems extracted into ./getNavItems for pure-function testing.
// See getNavItems.test.ts for the role visibility matrix assertion.
// Re-export here is intentional — keeps the existing AppShell.tsx call site.

/**
 * B9 — bottom nav (mobile) is a small subset of the sidebar, picking the most-used
 * tabs per role.
 *
 * Clock In appears for EVERY role. The sidebar is desktop-only (`hidden md:flex`),
 * so on a phone this bar is the entire navigation — a role missing from here can
 * reach nothing at all. it_admin used to be missing outright, and hr_admin had no
 * Clock In entry despite being an employee with shifts like anyone else.
 */
function getBottomNavItems(role: UserRole): NavItem[] {
  if (role === 'hr_admin') {
    return [
      { label: 'Home',     path: '/dashboard',  icon: Home,     moduleKey: 'dashboard' },
      { label: 'Clock In', path: '/clock-in',   icon: Clock,    moduleKey: 'clock_in' },
      { label: 'People',   path: '/people',     icon: Users,    moduleKey: 'people' },
      { label: 'Time Off', path: '/time-off',   icon: Calendar, moduleKey: 'time_off' },
      { label: 'Admin',    path: '/admin',      icon: Settings, moduleKey: 'reports' },
    ];
  }
  if (role === 'it_admin') {
    return [
      { label: 'Home',     path: '/dashboard',  icon: Home,     moduleKey: 'dashboard' },
      { label: 'Clock In', path: '/clock-in',   icon: Clock,    moduleKey: 'clock_in' },
      { label: 'Time Off', path: '/time-off',   icon: Calendar, moduleKey: 'time_off' },
      { label: 'Admin',    path: '/admin',      icon: Settings, moduleKey: 'reports' },
    ];
  }
  if (role === 'manager') {
    return [
      { label: 'Home',     path: '/dashboard',  icon: Home,     moduleKey: 'dashboard' },
      { label: 'Clock In', path: '/clock-in',   icon: Clock,    moduleKey: 'clock_in' },
      { label: 'Time Off', path: '/time-off',   icon: Calendar, moduleKey: 'time_off' },
      { label: 'People',   path: '/people',     icon: Users,    moduleKey: 'people' },
    ];
  }
  // staff
  return [
    { label: 'Home',     path: '/dashboard',  icon: Home,     moduleKey: 'dashboard' },
    { label: 'Clock In', path: '/clock-in',   icon: Clock,    moduleKey: 'clock_in' },
    { label: 'Time Off', path: '/time-off',   icon: Calendar, moduleKey: 'time_off' },
    { label: 'Perf & Pay', path: '/performance-pay', icon: Trophy, moduleKey: 'performance' },
  ];
}

// super_admin is the only role left out: a platform operator with no tenant, branch
// or shift, confined to the /admin/tenants area by ProtectedRoute.
const BOTTOM_NAV_ROLES: UserRole[] = ['staff', 'manager', 'hr_admin', 'it_admin'];

export default function AppShell({ children }: AppShellProps) {
  const { user, logout, refreshUser } = useAuth();
  const { isModuleAllowed } = useModulePermissions();
  const company = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const [hasClockedIn, setHasClockedIn] = useState<boolean | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingLeaveCount, setPendingLeaveCount] = useState(0);
  const [hiringNewCount, setHiringNewCount] = useState(0);
  const [unreadAnnouncementCount, setUnreadAnnouncementCount] = useState(0);
  // Personal notices under the bell (for example, HR adding past leave to your record).
  const [notices, setNotices] = useState<UserNotice[]>([]);
  const [unreadNoticeCount, setUnreadNoticeCount] = useState(0);
  // B6 — number of open offboarding tasks assigned to the caller. Drives
  // the sidebar Offboarding badge for manager + hr_admin. Polled like the
  // other counts; also refreshed when task state changes in-app (future hook).
  const [offboardingTasksCount, setOffboardingTasksCount] = useState(0);
  // Nav hover is CSS now (hover:bg-muted), so no hover state is tracked in React.
  const [bellOpen, setBellOpen] = useState(false);
  // The element that opened the bell popover. State, not a ref: ShellBrandBar
  // renders a bell in both the sidebar header and the mobile top bar, and only
  // one is visible at a time -- a shared ref would anchor off whichever mounted
  // last, and reading a rect from the display:none copy yields zeros.
  const [bellEl, setBellEl] = useState<HTMLButtonElement | null>(null);
  const [bellAnchor, setBellAnchor] = useState<{ top: number; left: number } | null>(null);
  const [showTour, setShowTour] = useState(false);
  // Guards the wizard-complete PATCH. The wizard is marked as soon as it is
  // displayed, so this keeps it to a single request per mount.
  const wizardMarked = useRef(false);

  // B2 — top-right avatar dropdown. Separate ref/state from the bell so one
  // can be open without closing the other unintentionally.
  // B8 — dropdown now renders via React portal to document.body with
  // `position: fixed` + viewport coordinates computed from the button's
  // bounding rect. This escapes any ancestor with overflow:hidden (which
  // was clipping the menu inside the 220px sidebar in its original
  // `position: absolute; right: 0` form). avatarRef points to the BUTTON
  // (not its wrapper) so we can read getBoundingClientRect() on open.
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const [avatarAnchor, setAvatarAnchor] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!bellOpen) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const insideTrigger = bellEl !== null && bellEl.contains(target);
      const insideMenu = target.closest('[data-bell-dropdown="true"]') !== null;
      if (!insideTrigger && !insideMenu) setBellOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setBellOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [bellOpen, bellEl]);

  useEffect(() => {
    if (!bellOpen || !bellEl) { setBellAnchor(null); return; }
    function recompute() {
      const r = bellEl!.getBoundingClientRect();
      setBellAnchor({ top: r.bottom + 4, left: Math.max(8, r.right - 280) });
    }
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [bellOpen, bellEl]);

  // Close avatar dropdown on outside click + Escape. Because the menu is
  // portaled to document.body (B8), we can't rely on a single ref to
  // determine "inside the dropdown". We tag the portaled menu with
  // data-avatar-dropdown and accept clicks inside either the trigger OR
  // the menu as "inside".
  useEffect(() => {
    if (!avatarOpen) return;
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const triggerEl = avatarRef.current;
      const insideTrigger = triggerEl && triggerEl.contains(target);
      const insideMenu = target.closest('[data-avatar-dropdown="true"]') !== null;
      if (!insideTrigger && !insideMenu) {
        setAvatarOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setAvatarOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [avatarOpen]);

  // B8 — recompute viewport-fixed menu position when the dropdown opens
  // or on window resize while it's open. We read the button's bounding
  // rect (not its wrapper) so the menu lands precisely under the button
  // regardless of sidebar width or content above.
  useEffect(() => {
    if (!avatarOpen) return;
    function recompute() {
      const btn = avatarButtonRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const a = computeDropdownAnchor({
        trigger: { top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height },
        menuWidth: 220,
        menuHeight: 240,        // approximate; conservative for flip-above decision
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      });
      setAvatarAnchor({ top: a.top, left: a.left });
    }
    recompute();
    window.addEventListener('resize', recompute);
    return () => { window.removeEventListener('resize', recompute); };
  }, [avatarOpen]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetch = () => {
      getTodayRecord()
        .then((record) => {
          if (!cancelled) {
            setHasClockedIn(record !== null && record.clock_in_at !== null);
          }
        })
        .catch(() => {
          if (!cancelled) setHasClockedIn(null);
        });
    };

    fetch();
    const iv = setInterval(fetch, 60_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user || user.role === 'super_admin') return;
    let cancelled = false;

    const fetch = () => {
      getMyNotices()
        .then((r) => {
          if (!cancelled) { setNotices(r.notices); setUnreadNoticeCount(r.unread); }
        })
        .catch(() => {
          // The bell still works without notices.
        });
    };

    fetch();
    const iv = setInterval(fetch, 60_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user || !MANAGER_ROLES.includes(user.role)) return;
    let cancelled = false;

    const fetch = () => {
      getWfhPendingRequests()
        .then((requests) => {
          if (!cancelled) setPendingCount(requests.length);
        })
        .catch(() => {
          if (!cancelled) setPendingCount(0);
        });
    };

    fetch();
    const iv = setInterval(fetch, 60_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user || !LEAVE_ROLES.includes(user.role)) return;
    if (!MANAGER_ROLES.includes(user.role)) return; // only managers/HR see pending badge
    let cancelled = false;

    const fetch = () => {
      getLeavePendingRequests()
        .then((requests) => {
          if (!cancelled) setPendingLeaveCount(requests.length);
        })
        .catch(() => {
          if (!cancelled) setPendingLeaveCount(0);
        });
    };

    fetch();
    const iv = setInterval(fetch, 60_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user || (user.role !== 'hr_admin' && user.role !== 'manager')) return;
    let cancelled = false;
    getJobPostings('open')
      .then(jobs => {
        if (!cancelled) {
          const total = jobs.reduce((sum, j) => sum + (j.new_candidate_count ?? 0), 0);
          setHiringNewCount(total);
        }
      })
      .catch(() => {
        if (!cancelled) setHiringNewCount(0);
      });
    return () => { cancelled = true; };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchUnread = () => {
      apiClient.get<{ data: { count: number } }>('/api/announcements/unread-count')
        .then(res => {
          if (!cancelled) setUnreadAnnouncementCount(res.data.data.count ?? 0);
        })
        .catch(() => {
          if (!cancelled) setUnreadAnnouncementCount(0);
        });
    };

    fetchUnread();
    const iv = setInterval(fetchUnread, 60_000);

    // A6: the Announcements page fires these events when a card is marked
    // read so the sidebar badge drops immediately, instead of waiting up to
    // 60s for the next poll. Without this, the badge felt stuck, which the
    // manual pass read as "mark-as-read is broken" even though the server
    // call was succeeding.
    const onOneRead = () => {
      if (!cancelled) setUnreadAnnouncementCount((n) => Math.max(0, n - 1));
    };
    const onAllRead = () => {
      if (!cancelled) setUnreadAnnouncementCount(0);
    };
    document.addEventListener('attendza:announcement-read', onOneRead);
    document.addEventListener('attendza:announcements-all-read', onAllRead);

    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener('attendza:announcement-read', onOneRead);
      document.removeEventListener('attendza:announcements-all-read', onAllRead);
    };
  }, [user, location.pathname]);

  // B6 — poll the caller's open-offboarding-task count for the sidebar badge.
  // Only manager + hr_admin see the Offboarding nav item; only poll for them.
  useEffect(() => {
    if (!user) return;
    if (user.role !== 'manager' && user.role !== 'hr_admin') return;
    let cancelled = false;
    const fetchCount = () => {
      apiClient.get<{ data: Array<unknown> }>('/api/offboarding/my-tasks')
        .then((res) => {
          if (!cancelled) setOffboardingTasksCount(res.data.data?.length ?? 0);
        })
        .catch(() => {
          if (!cancelled) setOffboardingTasksCount(0);
        });
    };
    fetchCount();
    const iv = setInterval(fetchCount, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, location.pathname]);

  // Start tour for users who haven't completed it yet.
  // Use == null (loose) so undefined (field missing from stale login response) is treated
  // the same as null — avoids needing a page refresh after backend code changes.
  useEffect(() => {
    if (!user || user.wizard_completed_at != null || !isDesktop) return;
    // Once per browser session per user, whether or not the save below works.
    // Without this a failed save, or a resize that re-runs this effect, reopened
    // the tour in the same session.
    const seenKey = `attendza_wizard_shown_${user.id}`;
    if (readSessionFlag(seenKey)) return;
    const t = setTimeout(() => {
      writeSessionFlag(seenKey);
      setShowTour(true);
      // Mark on display rather than on finish. If we only marked it in
      // handleTourComplete, abandoning the tour (closing the tab, navigating
      // away, a refresh) would leave wizard_completed_at NULL and the wizard
      // would return on the next login. Marking here makes it first-login-only.
      // refreshUser is deferred to handleTourComplete so the resulting auth
      // state change cannot re-render the overlay mid-run.
      if (!wizardMarked.current) {
        wizardMarked.current = true;
        apiClient.patch('/api/profile/me/wizard-complete', {}).catch((err: unknown) => {
          // Persisting failed. Say so, and clear the guard so handleTourComplete
          // retries; the session flag above already stops the tour reopening.
          console.error('[AppShell] could not save that the welcome tour was shown:', err);
          wizardMarked.current = false;
        });
      }
    }, 500);
    return () => clearTimeout(t);
  }, [user, user?.wizard_completed_at, isDesktop]);

  async function handleTourComplete() {
    setShowTour(false);
    try {
      if (!wizardMarked.current) {
        wizardMarked.current = true;
        await apiClient.patch('/api/profile/me/wizard-complete', {});
      }
    } catch (err) {
      // Not fatal for this session (the tour will not reopen), but do not hide it.
      console.error('[AppShell] could not save that the welcome tour was completed:', err);
      wizardMarked.current = false;
      return;
    }
    try {
      // Only syncing the flag just saved: a failed reload must not sign the person out.
      await refreshUser({ keepSessionOnError: true }); // syncs wizard_completed_at into auth state
    } catch (err) {
      console.error('[AppShell] could not reload the user after the welcome tour (session kept):', err);
    }
  }

  // No user (auth screens, or the moment before /me resolves) and the
  // chrome-less full-screen flows both render the page on its own.
  if (!user) return <>{children}</>;
  if (SHELL_LESS_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'))) {
    return <>{children}</>;
  }

  // ── Platform-administration shell for super_admin ───────────────────────────
  if (user.role === 'super_admin') {
    return (
      <div className="flex min-h-dvh bg-background">
        {/* Width stays an inline 220px: the portal anchor maths in this file is
            derived from it, and two e2e specs assert against that geometry. */}
        <aside
          className="fixed inset-y-0 left-0 z-50 flex flex-col border-r bg-card"
          style={{ width: 220 }}
        >
          {/* Not ShellBrandBar: this shell has no notification bell to hang the
              controls off, and the brand mark is read-only here — super_admin has
              no tenant of its own, so this always renders the platform monogram.
              It edits tenant logos from the Tenants page instead. The theme
              toggle is still offered, otherwise this role could never switch. */}
          <div className="flex h-14 flex-shrink-0 items-center gap-2.5 border-b px-4">
            <div className="flex size-8 flex-shrink-0 items-center justify-center rounded-md bg-primary">
              {company.logoUrl ? (
                <img src={company.logoUrl} alt={company.name} className="size-7 rounded-sm object-contain" />
              ) : (
                <span className="font-heading text-13 font-bold text-primary-foreground">{company.short}</span>
              )}
            </div>
            <div className="flex-1" />
            <ModeToggle compact />
          </div>

          {/* Super-admin nav */}
          <nav className="flex-1 overflow-y-auto px-1 py-2">
            {getNavItems(user.role, user.id).map((item) => {
               const Icon = item.icon;
               const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
               return (
                 <button
                   key={item.label}
                   onClick={() => navigate(item.path)}
                   className={[
                     'mb-0.5 flex h-11 w-full cursor-pointer items-center gap-2.5 rounded-md px-3 text-left',
                     active
                       ? 'bg-primary text-primary-foreground'
                       : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                   ].join(' ')}
                 >
                   <Icon size={18} strokeWidth={active ? 2.5 : 1.8} className="shrink-0" />
                   <span className={`font-body text-sm ${active ? 'font-semibold' : 'font-normal'}`}>
                     {item.label}
                   </span>
                 </button>
               );
             })}
          </nav>

          <div className="flex-shrink-0 border-t p-3">
            <p className="m-0 mb-2 truncate font-body text-xs text-muted-foreground">
              {user.email}
            </p>
            <button
              onClick={handleLogout}
              className="flex h-[34px] w-full cursor-pointer items-center justify-center gap-1.5 rounded-md border bg-transparent font-body text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <LogOut size={13} />
              Sign out
            </button>
          </div>
        </aside>

        <main className="min-h-dvh flex-1 overflow-y-auto" style={{ marginLeft: 220 }}>
          {children}
        </main>
      </div>
    );
  }

  const showBottomNav = BOTTOM_NAV_ROLES.includes(user.role);
  const initials = getUserInitials(user.full_name);
  const firstName = user.full_name.split(' ')[0] ?? user.full_name;
  const roleLabel = getRoleLabel(user.role);
  const sidebarNavItems = getNavItems(user.role, user.id, user.is_recruiter)
    .filter((item) => !item.moduleKey || isModuleAllowed(item.moduleKey));
  // Same module filter as the sidebar above. Without it, switching a module off hid
  // the desktop entry but left the phone one in place, sending the tap to a 403.
  const bottomNavItems = getBottomNavItems(user.role)
    .filter((item) => !item.moduleKey || isModuleAllowed(item.moduleKey));

  async function handleLogout() {
    try {
      await logout();
    } catch {
      // logout navigates regardless
    }
  }

  function isNavActive(path: string): boolean {
    // B9 — each top-level tab stays active across its hub's nested routes
    // (e.g. /people/offboarding, /people/:id, /admin/audit-logs, etc.).
    if (path === '/people')          return location.pathname.startsWith('/people');
    if (path === '/time-off')        return location.pathname.startsWith('/time-off');
    if (path === '/hiring')          return location.pathname.startsWith('/hiring');
    if (path === '/performance-pay') return location.pathname.startsWith('/performance-pay');
    if (path === '/admin/audit-logs') return location.pathname.startsWith('/admin/audit-logs');
    if (path === '/admin') {
      return location.pathname.startsWith('/admin')
        && !location.pathname.startsWith('/admin/audit-logs');
    }
    // Everything else: exact match only
    return location.pathname === path;
  }

  // ── One layout, CSS breakpoints ──────────────────────────────────────────────
  // Sidebar and bottom nav are both always mounted; the `md:` prefix decides
  // which is visible. This replaced a `useIsDesktop()` fork that returned two
  // separate render trees: dragging a window across 768px unmounted and
  // remounted the whole shell, and the two copies had drifted apart.
  const totalBadge = bellBadgeTotal({ pendingWfh: pendingCount, pendingLeave: pendingLeaveCount, unreadAnnouncements: unreadAnnouncementCount, unreadNotices: unreadNoticeCount });

  function openNotice(n: UserNotice) {
    setBellOpen(false);
    if (!n.read_at) {
      const next = withNoticeRead({ notices, unread: unreadNoticeCount }, n.id, new Date().toISOString());
      setNotices(next.notices);
      setUnreadNoticeCount(next.unread);
      void markNoticeRead(n.id).catch(() => undefined);
    }
    if (n.link) navigate(n.link);
  }

  function readAllNotices() {
    const next = withAllNoticesRead({ notices, unread: unreadNoticeCount }, new Date().toISOString());
    setNotices(next.notices);
    setUnreadNoticeCount(next.unread);
    void markAllNoticesRead().catch(() => undefined);
  }

  // Captures which bell was clicked (sidebar vs mobile bar) so the popover
  // anchors to the visible one. Re-clicking the same button closes it.
  function handleBellClick(el: HTMLButtonElement) {
    setBellEl(el);
    setBellOpen((open) => !(open && bellEl === el));
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background" style={SHELL_VARS}>
      <aside
        id="tour-sidebar"
        className="fixed inset-y-0 left-0 z-50 hidden w-[var(--app-sidebar-w)] flex-col border-r bg-card md:flex"
      >
        {/* Brand + controls. Shared with the mobile top bar below so the two
            cannot drift; it owns the logo (editable for admins), the bell and
            the theme switcher. */}
        <div className="border-b">
          <ShellBrandBar onBellClick={handleBellClick} badgeCount={totalBadge} />
        </div>

        {/* Nav section */}
        <nav style={{ flex: 1, padding: '8px 4px', overflowY: 'auto', overflowX: 'hidden' }}>
          {sidebarNavItems.map((item) => {
            const Icon = item.icon;
            const active = isNavActive(item.path);
            const isTimeOff = item.path === '/time-off';
            const isHiring = item.path === '/hiring';
            const isPeople = item.path === '/people';
            const showLeaveBadge = isTimeOff && pendingLeaveCount > 0;
            const showHiringBadge = isHiring && hiringNewCount > 0;
            const showOffboardingBadge = isPeople && offboardingTasksCount > 0;
            const hasDot = showLeaveBadge || showHiringBadge || showOffboardingBadge;
            const badgeCount = showLeaveBadge ? pendingLeaveCount : showHiringBadge ? hiringNewCount : showOffboardingBadge ? offboardingTasksCount : 0;
            const badgeBg = showLeaveBadge ? 'var(--chart-4)' : showHiringBadge ? 'var(--destructive)' : 'var(--warning)';

            const tourNavId: Record<string, string> = {
              '/clock-in':  'tour-nav-clock-in',
              '/time-off':  'tour-nav-time-off',
              '/training':  'tour-nav-training',
            };

            return (
              <button
                key={item.path}
                id={tourNavId[item.path]}
                onClick={() => navigate(item.path)}
                className={[
                  'mb-0.5 flex h-11 w-full cursor-pointer items-center gap-2.5 rounded-md border-none px-3 text-left',
                  active
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
                ].join(' ')}
                aria-current={active ? 'page' : undefined}
              >
                {/* No `color` prop — the icon inherits currentColor from the
                    button, so it follows the hover/active state for free. */}
                <Icon size={18} strokeWidth={active ? 2.5 : 1.8} className="shrink-0" />

                <span
                  className={`flex-1 truncate font-body text-sm ${active ? 'font-semibold' : 'font-normal'}`}
                >
                  {item.label}
                </span>

                {hasDot && (
                  <span style={{
                    minWidth: 18, height: 18, borderRadius: 9,
                    backgroundColor: active ? 'var(--primary-foreground)' : badgeBg,
                    color: active ? badgeBg : 'var(--primary-foreground)',
                    fontSize: 10, fontWeight: 700,
                    fontFamily: '"Inter", sans-serif',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 4px', flexShrink: 0,
                  }}>
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div id="tour-avatar" ref={avatarRef} style={{ borderTop: '1px solid var(--border)', padding: 12, flexShrink: 0 }}>
          <button
            ref={avatarButtonRef}
            onClick={() => setAvatarOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={avatarOpen}
            aria-label={`Account menu for ${user.full_name}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              marginBottom: 10,
              padding: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 8,
            }}
          >
            <div style={{
              width: 36, height: 36,
              borderRadius: '50%',
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 13,
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: 700,
              flexShrink: 0,
            }}>
              {initials}
            </div>
            <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
              <p style={{
                fontFamily: '"Inter", sans-serif',
                fontSize: 13, fontWeight: 700, color: 'var(--primary)',
                margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {firstName}
              </p>
              <p style={{
                fontFamily: '"Inter", sans-serif',
                fontSize: 11, color: 'var(--muted-foreground)', margin: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {user.job_title ?? roleLabel}
              </p>
            </div>
          </button>
          {/* No Sign out button and no theme row here any more. Signing out is
              reached by clicking your name above, which opens the account menu
              that already carries it; the theme switcher moved up to the brand
              bar beside the bell. handleLogout is still used by that menu item
              and by the mobile bottom nav. */}
        </div>
      </aside>

      {/* Bell dropdown — portaled to body, fixed position */}
      {bellOpen && bellAnchor && createPortal(
        <div
          data-bell-dropdown="true"
          style={{
            position: 'fixed',
            top: bellAnchor.top,
            left: bellAnchor.left,
            width: 280,
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            boxShadow: '0 12px 32px rgba(0,0,0,0.14)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px 10px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: 14, color: 'var(--foreground)' }}>
              Notifications
            </span>
            <button onClick={() => setBellOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-foreground)', display: 'flex', borderRadius: 4, padding: 2 }}>
              <X size={14} />
            </button>
          </div>
          {/* Items */}
          <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 420, overflowY: 'auto' }}>
            {notices.slice(0, 8).map((n) => (
              <button
                key={n.id}
                type="button"
                data-notice-id={n.id}
                onClick={() => openNotice(n)}
                style={{ display: 'block', width: '100%', padding: '10px 12px', background: n.read_at ? 'var(--background)' : 'color-mix(in oklab, var(--info) 12%, var(--background))', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}
              >
                <div style={{ fontFamily: '"Inter", sans-serif', fontSize: 13, fontWeight: n.read_at ? 500 : 700, color: 'var(--foreground)' }}>{n.title}</div>
                {n.body && <div style={{ fontFamily: '"Inter", sans-serif', fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2, lineHeight: 1.4 }}>{n.body}</div>}
              </button>
            ))}
            {unreadNoticeCount > 0 && (
              <button type="button" onClick={readAllNotices} style={{ alignSelf: 'flex-end', background: 'none', border: 'none', cursor: 'pointer', fontFamily: '"Inter", sans-serif', fontSize: 12, color: 'var(--primary)', padding: '0 2px' }}>
                Mark all as read
              </button>
            )}
            {unreadAnnouncementCount > 0 && (
              <button onClick={() => { navigate('/announcements'); setBellOpen(false); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px 12px', background: 'color-mix(in oklab, var(--info) 12%, var(--background))', border: '1px solid rgba(27,58,107,0.15)', borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
                <div>
                  <div style={{ fontFamily: '"Inter", sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--foreground)' }}>Announcements</div>
                  <div style={{ fontFamily: '"Inter", sans-serif', fontSize: 11, color: 'var(--muted-foreground)', marginTop: 1 }}>unread</div>
                </div>
                <span style={{ background: 'var(--primary)', color: 'var(--primary-foreground)', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700, fontFamily: '"Inter", sans-serif', flexShrink: 0 }}>{unreadAnnouncementCount}</span>
              </button>
            )}
            {pendingLeaveCount > 0 && (
              <button onClick={() => { navigate('/leave'); setBellOpen(false); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px 12px', background: 'color-mix(in oklab, var(--warning) 12%, var(--background))', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
                <div>
                  <div style={{ fontFamily: '"Inter", sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--foreground)' }}>Leave requests</div>
                  <div style={{ fontFamily: '"Inter", sans-serif', fontSize: 11, color: 'var(--muted-foreground)', marginTop: 1 }}>awaiting review</div>
                </div>
                <span style={{ background: 'var(--warning)', color: 'var(--primary-foreground)', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700, fontFamily: '"Inter", sans-serif', flexShrink: 0 }}>{pendingLeaveCount}</span>
              </button>
            )}
            {pendingCount > 0 && (
              <button onClick={() => { navigate('/requests'); setBellOpen(false); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px 12px', background: 'color-mix(in oklab, var(--info) 12%, var(--background))', border: '1px solid rgba(14,165,233,0.2)', borderRadius: 8, cursor: 'pointer', textAlign: 'left' }}>
                <div>
                  <div style={{ fontFamily: '"Inter", sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--foreground)' }}>WFH requests</div>
                  <div style={{ fontFamily: '"Inter", sans-serif', fontSize: 11, color: 'var(--muted-foreground)', marginTop: 1 }}>awaiting review</div>
                </div>
                <span style={{ background: 'var(--info)', color: 'var(--primary-foreground)', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700, fontFamily: '"Inter", sans-serif', flexShrink: 0 }}>{pendingCount}</span>
              </button>
            )}
            {totalBadge === 0 && notices.length === 0 && (
              <div style={{ padding: '16px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Bell size={16} className="text-muted-foreground" />
                </div>
                <p style={{ fontFamily: '"Inter", sans-serif', fontSize: 13, color: 'var(--muted-foreground)', margin: 0, fontWeight: 500 }}>
                  All caught up
                </p>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Avatar dropdown — portaled to body */}
      {avatarOpen && avatarAnchor && createPortal(
        <div
          role="menu"
          data-avatar-dropdown="true"
          style={{
            position: 'fixed',
            top: avatarAnchor.top,
            left: avatarAnchor.left,
            width: 220,
            background: 'var(--card)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', fontFamily: '"Inter", sans-serif' }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--foreground)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.full_name}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--muted-foreground)' }}>{roleLabel}</p>
          </div>
          {[
            { label: 'My Info',          path: '/my-info' },
            { label: 'History',          path: '/account/history' },
            { label: 'Account Settings', path: '/account' },
            ...((user.role === 'hr_admin' || user.role === 'it_admin')
              ? [{ label: 'Admin Settings', path: '/admin/settings' }]
              : []),
          ].map((item) => (
            <button
              key={item.path}
              role="menuitem"
              onClick={() => { navigate(item.path); setAvatarOpen(false); }}
              className="w-full cursor-pointer border-none border-b bg-transparent px-3 py-2.5 text-left font-body text-13 text-foreground hover:bg-muted"
            >
              {item.label}
            </button>
          ))}
          <button
            role="menuitem"
            onClick={() => { setAvatarOpen(false); void handleLogout(); }}
            className="w-full cursor-pointer border-none bg-transparent px-3 py-2.5 text-left font-body text-13 font-medium text-destructive hover:bg-destructive/10"
          >
            Sign out
          </button>
        </div>,
        document.body
      )}

      {/* Mobile top bar. The sidebar is hidden below md, so without this the
          logo, notifications and theme switcher are unreachable on a phone.
          Sticky rather than fixed so it participates in layout and <main> needs
          no compensating offset. z-40 keeps it under the portalled dropdowns
          (z-1000) and the bottom nav (z-50). */}
      <header className="sticky top-0 z-40 border-b bg-card md:hidden">
        <ShellBrandBar onBellClick={handleBellClick} badgeCount={totalBadge} />
      </header>

      <main
        id="tour-main-content"
        className={[
          'min-h-dvh flex-1 overflow-y-auto md:ml-[var(--app-sidebar-w)]',
          // Clearance for the fixed bottom nav, dropped at the same breakpoint
          // the nav itself is. Pages must NOT add bottom padding on top of this
          // — ten of them used to, which gave 176px of dead space.
          showBottomNav ? 'pb-20 md:pb-0' : '',
        ].join(' ')}
      >
        <TwoFactorGraceBanner />
        {children}
      </main>

      {showBottomNav && (
        <nav
          className="fixed inset-x-0 bottom-0 z-50 flex items-start justify-around border-t bg-card md:hidden"
          style={{
            // Inline: env() safe-area insets have no Tailwind utility.
            height: 'calc(64px + env(safe-area-inset-bottom))',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          {bottomNavItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            const isClockIn = item.path === '/clock-in';
            const isRequests = item.path === '/requests';
            const isLeaveTab = item.path === '/leave';
            const showClockInDot = isClockIn && hasClockedIn === false;
            const showPendingBadge = isRequests && pendingCount > 0;
            const showLeaveBadgeTab = isLeaveTab && pendingLeaveCount > 0;

            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  flex: 1,
                  height: 64,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4,
                  color: active ? 'var(--primary)' : 'var(--muted-foreground)',
                  minWidth: 48,
                  position: 'relative',
                }}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
              >
                <div style={{ position: 'relative' }}>
                  <Icon size={22} strokeWidth={active ? 2.5 : 1.8} />
                  {showClockInDot && (
                    <span
                      title="You have not clocked in yet"
                      style={{
                        position: 'absolute',
                        top: -3,
                        right: -4,
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        backgroundColor: 'var(--warning)',
                        border: '1.5px solid var(--card)',
                      }}
                    />
                  )}
                  {showPendingBadge && (
                    <span
                      style={{
                        position: 'absolute', top: -6, right: -8,
                        minWidth: 16, height: 16, borderRadius: 8,
                        backgroundColor: 'var(--destructive)', border: '1.5px solid var(--card)',
                        color: 'var(--primary-foreground)', fontSize: 10, fontWeight: 700,
                        fontFamily: '"Inter", sans-serif',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0 3px',
                      }}
                    >
                      {pendingCount > 9 ? '9+' : pendingCount}
                    </span>
                  )}
                  {showLeaveBadgeTab && (
                    <span
                      style={{
                        position: 'absolute', top: -6, right: -8,
                        minWidth: 16, height: 16, borderRadius: 8,
                        backgroundColor: 'var(--chart-4)', border: '1.5px solid var(--card)',
                        color: 'var(--primary-foreground)', fontSize: 10, fontWeight: 700,
                        fontFamily: '"Inter", sans-serif',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0 3px',
                      }}
                    >
                      {pendingLeaveCount > 9 ? '9+' : pendingLeaveCount}
                    </span>
                  )}
                </div>
                <span
                  style={{
                    fontSize: 11,
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}

          {/* Sign out in bottom nav */}
          <button
            onClick={handleLogout}
            style={{
              flex: 1,
              height: 64,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              color: 'var(--muted-foreground)',
              minWidth: 48,
            }}
            aria-label="Sign out"
          >
            <LogOut size={22} strokeWidth={1.8} />
            <span style={{ fontSize: 11, fontFamily: '"Inter", sans-serif' }}>Sign out</span>
          </button>
        </nav>
      )}

      {/* First-run tour. Desktop-only: every step after the welcome card points
          at a sidebar item, which is display:none below md. */}
      {showTour && (
        <TourOverlay
          hasTraining={isModuleAllowed('training')}
          onComplete={handleTourComplete}
        />
      )}
    </div>
  );
}
